// src/components/shared/ChartComponents.js
import React from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { formatCurrency, formatNumber } from '../../utils/dashboardUtils';

// Chart colors from design system
export const CHART_COLORS = {
  primary: '#D02E17',      // Red - Leads/Revenue
  secondary: '#31B9CE',    // Cyan - Unique/Secondary metrics
  tertiary: '#5F5E5E',     // Gray - Total calls/Base metrics
  quaternary: '#94a3b8',   // Light gray
  success: '#10B981',      // Green
  warning: '#F59E0B',      // Orange
  light: '#FCA5A5'         // Light red for comparisons
};

// Custom tooltip styling
const CustomTooltip = ({ active, payload, label, formatter, labelFormatter }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div style={{
      background: 'white',
      border: '1px solid var(--color-neutral-200)',
      borderRadius: 'var(--radius-lg)',
      padding: '12px 16px',
      boxShadow: 'var(--shadow-lg)',
      fontSize: '13px'
    }}>
      <p style={{
        fontWeight: 600,
        color: 'var(--color-neutral-700)',
        marginBottom: '8px',
        borderBottom: '1px solid var(--color-neutral-100)',
        paddingBottom: '6px'
      }}>
        {labelFormatter ? labelFormatter(label) : label}
      </p>
      {payload.map((entry, index) => (
        <div key={index} style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: index > 0 ? '4px' : 0
        }}>
          <span style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: entry.color
          }} />
          <span style={{ color: 'var(--color-neutral-600)' }}>
            {formatter ? formatter(entry.value, entry.name)[1] : entry.name}:
          </span>
          <span style={{ fontWeight: 600, color: 'var(--color-neutral-800)' }}>
            {formatter ? formatter(entry.value, entry.name)[0] : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// Get chart title based on granularity
const getChartTitle = (baseTitle, granularity, isCumulative = false) => {
  const granularityLabel = {
    'day': 'DAGELIJKS',
    'week': 'WEKELIJKS',
    'month': 'MAANDELIJKS'
  }[granularity] || 'DAGELIJKS';

  const periodLabel = {
    'day': '14 DAGEN',
    'week': '12 WEKEN',
    'month': '6 MAANDEN'
  }[granularity] || '';

  const viewType = isCumulative ? 'CUMULATIEF' : granularityLabel;

  return `${baseTitle} ${viewType} - ${periodLabel}`;
};

// Daily Calls Chart (LineChart version)
export const DailyCallsChart = React.memo(({ data = [], period, showComparison = false, granularity = 'day' }) => {
  const nameMapping = {
    'calls': 'Calls',
    'totalCalls': 'Calls',
    'unique': 'Unique',
    'leads': 'Leads',
    'leadCalls': 'Leads',
    'previousCalls': 'Vorige Periode Calls',
    'previousUnique': 'Vorige Periode Unique',
    'previousLeadCalls': 'Vorige Periode Leads'
  };

  const itemSorter = (item) => {
    const order = ['calls', 'totalCalls', 'unique', 'leads', 'leadCalls'];
    return order.indexOf(item.dataKey);
  };

  // Use 'label' field if available (from aggregation), otherwise fall back to dayName/date
  const hasLabel = data.length > 0 && data[0]?.label;
  const isWeekData = data.length > 0 && data[0]?.dayName;

  return (
    <div className="chart-card">
      <h3 className="chart-title">
        {getChartTitle('CALLS', granularity)}
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-neutral-200)" />
          <XAxis
            dataKey={hasLabel ? "label" : (isWeekData ? "dayName" : "date")}
            tick={{ fontSize: 12, fill: 'var(--color-neutral-500)' }}
            tickFormatter={hasLabel ? (v) => v : (isWeekData ? (v) => v : (date) => new Date(date + 'T12:00:00Z').toLocaleDateString('nl-NL', { month: 'numeric', day: 'numeric', timeZone: 'Europe/Amsterdam' }))}
            axisLine={{ stroke: 'var(--color-neutral-200)' }}
            tickLine={{ stroke: 'var(--color-neutral-200)' }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: 'var(--color-neutral-500)' }}
            axisLine={{ stroke: 'var(--color-neutral-200)' }}
            tickLine={{ stroke: 'var(--color-neutral-200)' }}
          />
          <Tooltip
            content={<CustomTooltip
              formatter={(value, name) => [formatNumber(value), nameMapping[name] || name]}
              labelFormatter={(label) => label}
            />}
            itemSorter={itemSorter}
          />
          <Legend
            wrapperStyle={{ fontSize: '12px', paddingTop: '16px' }}
          />

          {/* Main lines */}
          <Line
            type="monotone"
            dataKey="calls"
            stroke={CHART_COLORS.tertiary}
            strokeWidth={2}
            name="Calls"
            dot={{ fill: CHART_COLORS.tertiary, r: 3 }}
            activeDot={{ r: 5, strokeWidth: 2 }}
          />
          <Line
            type="monotone"
            dataKey="unique"
            stroke={CHART_COLORS.secondary}
            strokeWidth={2}
            strokeDasharray="5 5"
            name="Unique"
            dot={{ r: 3, fill: CHART_COLORS.secondary }}
          />
          <Line
            type="monotone"
            dataKey="leadCalls"
            stroke={CHART_COLORS.primary}
            strokeWidth={3}
            name="Leads"
            dot={{ fill: CHART_COLORS.primary, r: 4 }}
            activeDot={{ r: 6, strokeWidth: 2 }}
          />

          {/* Comparison lines */}
          {showComparison && (
            <>
              <Line
                type="monotone"
                dataKey="previousCalls"
                stroke={CHART_COLORS.light}
                strokeWidth={2}
                strokeDasharray="5 5"
                name="Vorige Periode Calls"
              />
              <Line
                type="monotone"
                dataKey="previousUnique"
                stroke="#9CA3AF"
                strokeWidth={1}
                strokeDasharray="3 3"
                name="Vorige Periode Unique"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="previousLeadCalls"
                stroke={CHART_COLORS.light}
                strokeWidth={2}
                strokeDasharray="5 5"
                name="Vorige Periode Leads"
              />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});

// Cumulative Chart - Focused on Leads comparison
// Design: Minimalist, data-forward. Shows Leads as hero metric with clear period comparison.
export const CumulativeChart = React.memo(({ data = [], period, granularity = 'day', showComparison = false }) => {
  // Use 'label' field if available (from aggregation), otherwise fall back to dayName/date
  const hasLabel = data.length > 0 && data[0]?.label;
  const isWeekData = data.length > 0 && data[0]?.dayName;

  // Calculate summary stats for the compact metric display
  const latestData = data.length > 0 ? data[data.length - 1] : null;
  const currentLeads = latestData?.leadCalls || 0;
  const previousLeads = latestData?.previousLeadCalls || 0;
  const leadsDiff = currentLeads - previousLeads;
  const isAhead = leadsDiff > 0;

  // Sort tooltip: Leads first (most important), then Unique, then Calls
  const itemSorter = (item) => {
    const order = ['Leads', 'Vorige Leads', 'Unique', 'Calls'];
    const idx = order.indexOf(item.name);
    return idx === -1 ? 999 : idx;
  };

  return (
    <div className="chart-card">
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
        <h3 className="chart-title" style={{ margin: 0 }}>
          {getChartTitle('LEADS', granularity, true)}
        </h3>
        {/* Compact summary showing if ahead/behind */}
        {showComparison && latestData && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            color: isAhead ? '#10B981' : '#EF4444',
            fontWeight: 600
          }}>
            <span>{isAhead ? '▲' : '▼'} {isAhead ? '+' : ''}{formatNumber(leadsDiff)}</span>
            <span style={{ color: 'var(--color-neutral-400)', fontWeight: 400, fontSize: '11px' }}>vs vorige</span>
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="gradientLeadsCumulative" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_COLORS.primary} stopOpacity={0.4}/>
              <stop offset="100%" stopColor={CHART_COLORS.primary} stopOpacity={0.02}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-neutral-200)" vertical={false} />
          <XAxis
            dataKey={hasLabel ? "label" : (isWeekData ? "dayName" : "date")}
            tick={{ fontSize: 11, fill: 'var(--color-neutral-400)' }}
            tickFormatter={hasLabel ? (v) => v : (isWeekData ? (v) => v : (date) => new Date(date + 'T12:00:00Z').toLocaleDateString('nl-NL', { month: 'numeric', day: 'numeric', timeZone: 'Europe/Amsterdam' }))}
            axisLine={{ stroke: 'var(--color-neutral-200)' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'var(--color-neutral-400)' }}
            axisLine={false}
            tickLine={false}
            width={40}
          />
          <Tooltip
            content={<CustomTooltip
              formatter={(value, name) => [formatNumber(value), name]}
              labelFormatter={(label) => label}
            />}
            itemSorter={itemSorter}
          />
          <Legend
            wrapperStyle={{ fontSize: '11px', paddingTop: '12px' }}
            iconType="plainline"
          />

          {/* Previous period - subtle reference line (rendered first = behind) */}
          {showComparison && (
            <Area
              type="monotone"
              dataKey="previousLeadCalls"
              stroke="#9CA3AF"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              fill="none"
              name="Vorige Leads"
              dot={false}
            />
          )}

          {/* Current Leads - hero metric with gradient fill */}
          <Area
            type="monotone"
            dataKey="leadCalls"
            stroke={CHART_COLORS.primary}
            strokeWidth={2.5}
            fill="url(#gradientLeadsCumulative)"
            name="Leads"
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: 'white' }}
          />

          {/* Unique - secondary, line only */}
          <Line
            type="monotone"
            dataKey="unique"
            stroke={CHART_COLORS.secondary}
            strokeWidth={1.5}
            dot={false}
            name="Unique"
          />

          {/* Calls - tertiary, subtle line */}
          <Line
            type="monotone"
            dataKey="calls"
            stroke={CHART_COLORS.tertiary}
            strokeWidth={1}
            strokeOpacity={0.6}
            dot={false}
            name="Calls"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
});

// Revenue Chart - Focused design matching Leads chart
export const RevenueChart = React.memo(({ data = [], period, showComparison = true, isCumulative = false, granularity = 'day' }) => {
  // Use 'label' field if available (from aggregation), otherwise fall back to dayName/date
  const hasLabel = data.length > 0 && data[0]?.label;
  const isWeekData = data.length > 0 && data[0]?.dayName;
  const chartTitle = getChartTitle('REVENUE', granularity, isCumulative);

  // Calculate summary stats for the compact metric display
  const latestData = data.length > 0 ? data[data.length - 1] : null;
  const currentRevenue = latestData?.revenue || 0;
  const previousRevenue = latestData?.previousRevenue || 0;
  const revenueDiff = currentRevenue - previousRevenue;
  const isAhead = revenueDiff > 0;

  return (
    <div className="chart-card">
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
        <h3 className="chart-title" style={{ margin: 0 }}>
          {chartTitle}
        </h3>
        {/* Compact summary showing if ahead/behind */}
        {showComparison && isCumulative && latestData && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            color: isAhead ? '#10B981' : '#EF4444',
            fontWeight: 600
          }}>
            <span>{isAhead ? '▲' : '▼'} {isAhead ? '+' : ''}{formatCurrency(revenueDiff)}</span>
            <span style={{ color: 'var(--color-neutral-400)', fontWeight: 400, fontSize: '11px' }}>vs vorige</span>
          </div>
        )}
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="gradientRevenue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_COLORS.primary} stopOpacity={0.4}/>
              <stop offset="100%" stopColor={CHART_COLORS.primary} stopOpacity={0.02}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-neutral-200)" vertical={false} />
          <XAxis
            dataKey={hasLabel ? "label" : (isWeekData ? "dayName" : "date")}
            tick={{ fontSize: 11, fill: 'var(--color-neutral-400)' }}
            tickFormatter={hasLabel ? (v) => v : (isWeekData ? (v) => v : (date) => new Date(date + 'T12:00:00Z').toLocaleDateString('nl-NL', { month: 'numeric', day: 'numeric', timeZone: 'Europe/Amsterdam' }))}
            axisLine={{ stroke: 'var(--color-neutral-200)' }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(value) => formatCurrency(value)}
            tick={{ fontSize: 11, fill: 'var(--color-neutral-400)' }}
            axisLine={false}
            tickLine={false}
            width={55}
          />
          <Tooltip
            content={<CustomTooltip
              formatter={(value, name) => [formatCurrency(value), name]}
              labelFormatter={(label) => label}
            />}
          />
          <Legend
            wrapperStyle={{ fontSize: '11px', paddingTop: '12px' }}
            iconType="plainline"
          />

          {/* Previous period - subtle reference line (rendered first = behind) */}
          {showComparison && (
            <Area
              type="monotone"
              dataKey="previousRevenue"
              stroke="#9CA3AF"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              fill="none"
              name="Vorige Periode"
              dot={false}
            />
          )}

          {/* Current Revenue - hero metric with gradient fill */}
          <Area
            type="monotone"
            dataKey="revenue"
            stroke={CHART_COLORS.primary}
            strokeWidth={2.5}
            fill="url(#gradientRevenue)"
            name="Huidige Periode"
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: 'white' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
});

// Revenue by Niche (PieChart)
export const RevenueByNicheChart = React.memo(({ data = [] }) => {
  const chartColors = [CHART_COLORS.primary, CHART_COLORS.secondary, CHART_COLORS.tertiary, CHART_COLORS.quaternary];

  const renderCustomLabel = ({ name, value, percent, cx, cy, midAngle, innerRadius, outerRadius }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 1.4;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // Don't show label for very small slices

    return (
      <text
        x={x}
        y={y}
        fill="var(--color-neutral-600)"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: '11px', fontWeight: 500 }}
      >
        {`${name}: ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="chart-card">
      <h3 className="chart-title">REVENUE PER NICHE</h3>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={data}
            dataKey="revenue"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={90}
            innerRadius={40}
            labelLine={false}
            label={renderCustomLabel}
            strokeWidth={2}
            stroke="white"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
            ))}
          </Pie>
          <Tooltip
            content={<CustomTooltip
              formatter={(value) => [formatCurrency(value), 'Revenue']}
            />}
          />
          <Legend
            wrapperStyle={{ fontSize: '12px', paddingTop: '16px' }}
            formatter={(value, entry) => (
              <span style={{ color: 'var(--color-neutral-600)' }}>{value}</span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
});

// Call Classifications (BarChart)
export const ClassificationsChart = React.memo(({ data = [] }) => {
  const leadClassifications = ['Lead', 'Lead X', 'Lead gemist', '-', 'Verkoop'];

  return (
    <div className="chart-card">
      <h3 className="chart-title">CALL CLASSIFICATIONS</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} layout="vertical" margin={{ left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-neutral-200)" horizontal={true} vertical={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 12, fill: 'var(--color-neutral-500)' }}
            axisLine={{ stroke: 'var(--color-neutral-200)' }}
            tickLine={{ stroke: 'var(--color-neutral-200)' }}
          />
          <YAxis
            type="category"
            dataKey="type"
            width={130}
            tick={{ fontSize: 12, fill: 'var(--color-neutral-600)' }}
            axisLine={{ stroke: 'var(--color-neutral-200)' }}
            tickLine={false}
          />
          <Tooltip
            content={<CustomTooltip
              formatter={(value) => [formatNumber(value), 'Aantal calls']}
            />}
          />
          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
            {data.map((entry, index) => {
              const isLead = leadClassifications.includes(entry.type);
              return <Cell key={`cell-${index}`} fill={isLead ? CHART_COLORS.primary : CHART_COLORS.tertiary} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
});
